import styled from "styled-components";
import { SelectedItem, branch, folder, setting } from "shared/types/Report";
import TRI_SVG from "assets/images/tri.svg";
import useReleaseTable from "features/homework/hooks/useReleaseTable";
import FOLDER_YELLOW_SVG from "assets/images/folder_yellow.svg";
import FILE_SVG from "assets/images/file.svg";
import Modal from "shared/components/Modal";
import { EffectiveDate } from "shared/types/Homework";
import { SmallButton } from "shared/components/Button";
import LoadingSpinner from "shared/components/Spinner";
import React, { useState } from "react";
import { FiSearch } from "react-icons/fi";

interface ReleaseTableProps {
    // 기본 데이터
    branchList: branch[];
    selectedBranchList: string[];
    folderList: folder[];
    selectedItems: SelectedItem[];
    effectiveDate: EffectiveDate;
    
    // 핸들러들
    onSelectBranchClickHandler: (id: string, checked: boolean) => void;
    onSelectReportClickHandler: (folder: folder, isChecked: boolean, parentStart: string) => void;
    onEffectiveDateChangeHandler: (name: string, value: string) => void;
    
    // 옵션 설정
    effectiveDateSize?: EffectiveDate;
    showEndDate?: boolean;
    
    // 학습 설정 관련 (선택적)
    setting?: setting;
    onOpenSettingModalClickHandler?: () => void;
    onDeleteSettingClickHandler?: () => void;
    
    // 일정 관리 관련 (선택적)
    onOpenScheduleModalClickHandler?: () => void;
    onOpenDateScheduleModalClickHandler?: () => void;
    
    // 커스텀 헤더 (선택적)
    branchHeader?: string;
    reportHeader?: string;
    selectedHeader?: string;
    
    // 조건부 렌더링
    showBranchColumn?: boolean;
    showSettingSection?: boolean;
    showScheduleButtons?: boolean;
}

const ReleaseTable = ({ 
    branchList, 
    selectedBranchList, 
    onSelectBranchClickHandler, 
    onSelectReportClickHandler, 
    folderList, 
    selectedItems, 
    effectiveDate, 
    effectiveDateSize, 
    onOpenSettingModalClickHandler, 
    onEffectiveDateChangeHandler, 
    setting, 
    onDeleteSettingClickHandler, 
    onOpenScheduleModalClickHandler, 
    onOpenDateScheduleModalClickHandler, 
    showEndDate = true,
    branchHeader = "지점(클래스)",
    reportHeader = "과제",
    selectedHeader = "선택된 과제",
    showBranchColumn = true,
    showSettingSection = true,
    showScheduleButtons = true
}: ReleaseTableProps) => {
    const { 
        loading, 
        rotations, 
        onTriClickHandler,
        isOpenErrorModal, 
        onErrorModalConfirmHandler, 
        selectedFolderList
    } = useReleaseTable(selectedItems, folderList);

    const [search, setSearch] = useState("");

    // 이름순으로 정렬된 지점 리스트 (검색 적용)
    const filteredBranchList = branchList.filter(item => item.name.includes(search));
    const sortedBranchList = [...filteredBranchList].sort((a, b) => 
        a.name.localeCompare(b.name)
    );

    const viewBranchList = () => {
        if (!sortedBranchList.length) {
            return <EmptyHint>{search ? "검색 결과가 없습니다" : `${branchHeader}이 없습니다`}</EmptyHint>;
        }
        return sortedBranchList.map((item, index) => (
            <Cell key={index} $active={selectedBranchList.includes(item.id)}>
                <CheckBox 
                    id={item.id} 
                    type="checkbox" 
                    checked={selectedBranchList.includes(item.id)} 
                    onChange={(e) => onSelectBranchClickHandler(item.id, e.target.checked)} 
                />
                <Label htmlFor={item.id}>{item.name}</Label>
            </Cell>
        ));
    };

    // 폴더 내의 모든 과제 ID를 재귀적으로 가져오는 함수
    const getAllReportIds = (folder: folder): string[] => {
        const reportIds: string[] = [];
        
        if (folder.type === "report") {
            reportIds.push(folder.id);
        } else if (folder.subFolders) {
            folder.subFolders.forEach(subFolder => {
                reportIds.push(...getAllReportIds(subFolder));
            });
        }
        
        return reportIds;
    };

    // 폴더의 선택 상태를 계산하는 함수
    const getFolderSelectionState = (folder: folder): { checked: boolean; indeterminate: boolean } => {
        if (folder.type === "report") {
            const isSelected = selectedItems.some(selected => selected.id === folder.id);
            return { checked: isSelected, indeterminate: false };
        }
        
        if (!folder.subFolders || folder.subFolders.length === 0) {
            return { checked: false, indeterminate: false };
        }
        
        const allReportIds = getAllReportIds(folder);
        const selectedReportIds = selectedItems.map(item => item.id);
        
        const selectedCount = allReportIds.filter(id => selectedReportIds.includes(id)).length;
        
        if (selectedCount === 0) {
            return { checked: false, indeterminate: false };
        } else if (selectedCount === allReportIds.length) {
            return { checked: true, indeterminate: false };
        } else {
            return { checked: false, indeterminate: true };
        }
    };

    const viewFolderList = (folders: folder[], level: number = 1, isTopLevel: boolean = true, parentPath: string = "") => {
        if (showBranchColumn && !selectedBranchList.length) {
            return isTopLevel ? <EmptyHint>왼쪽에서 {branchHeader}를 선택하세요</EmptyHint> : null;
        }
        if (!folders.length) {
            return isTopLevel ? <EmptyHint>표시할 과제가 없습니다</EmptyHint> : null;
        }
    
        return folders.map((item, index) => {
            const isRotated = rotations[item.id];
            // 최상위 폴더의 경로 설정
            const currentPath = isTopLevel ? item.path : parentPath;
            const selectionState = getFolderSelectionState(item);
    
            return (
                <div key={item.id}>
                    <Cell style={{ paddingLeft: `${level * 10}px` }} $active={item.type === "report" && selectedItems.some(selected => selected.id === item.id)}>
                        {item.type === "folder" ? (
                            <Tri
                                src={TRI_SVG}
                                alt="tri"
                                onClick={() => onTriClickHandler(item.id)}
                                style={{ transform: isRotated ? 'rotate(90deg)' : 'rotate(0deg)' }}
                            />
                        ) : null}
                        <Icon src={item.type === "folder" ? FOLDER_YELLOW_SVG : FILE_SVG} alt="folder" />
                        {item.type === "folder" ? (
                            <CheckBox
                                id={item.id}
                                type="checkbox"
                                checked={selectionState.checked}
                                ref={(input) => {
                                    if (input) {
                                        input.indeterminate = selectionState.indeterminate;
                                    }
                                }}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    onSelectReportClickHandler(item, isChecked, currentPath || "");
                                }}
                            />
                        ) : (
                            <CheckBox
                                id={item.id}
                                type="checkbox"
                                checked={selectedItems.some(selected => selected.id === item.id)}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    onSelectReportClickHandler(item, isChecked, currentPath || "");
                                }}
                            />
                        )}
                        <Label htmlFor={item.id}>{item.name}</Label>
                    </Cell>
                    {isRotated && item.subFolders && viewFolderList(item.subFolders, level + 4, false, currentPath)}
                </div>
            );
        });
    };
    
    // 선택된 과제의 가장 가까운 상위 폴더를 찾는 함수
    const findParentFolder = (selectedItem: SelectedItem): folder | null => {
        const pathParts = selectedItem.path.split('/');
        const reportIndex = pathParts.lastIndexOf('Report');
        if (reportIndex > 0) {
            const folderId = pathParts[reportIndex - 1];
            const basePath = pathParts.slice(0, reportIndex - 1).join('/');

            const findInFolders = (folders: folder[]): folder | null => {
                for (const folder of folders) {
                    if (folder.id === folderId && folder.path === basePath) {
                        return folder;
                    }
                    if (folder.subFolders) {
                        const found = findInFolders(folder.subFolders);
                        if (found) return found;
                    }
                }
                return null;
            };
            return findInFolders(folderList);
        }
        
        const startPath = selectedItem.start;
        const findInFoldersByPath = (folders: folder[]): folder | null => {
            for (const folder of folders) {
                if (folder.path && (folder.path === startPath || startPath.endsWith(folder.path))) {
                    return folder;
                }
                if (folder.subFolders) {
                    const found = findInFoldersByPath(folder.subFolders);
                    if (found) return found;
                }
            }
            return null;
        };
        
        return findInFoldersByPath(folderList);
    };

    const renderSelectedReport = () => {
        if (!selectedItems.length) return <EmptyHint>선택한 과제가 여기에 표시됩니다</EmptyHint>;
        if (loading) return <LoadingSpinner width={300} />;

        const folderMap: Record<string, { folder: folder, items: SelectedItem[] }> = {};
        selectedItems.forEach(item => {
            const parent = findParentFolder(item);
            if (parent) {
                const key = parent.id + '___' + parent.path;
                if (!folderMap[key]) {
                    folderMap[key] = { folder: parent, items: [] };
                }
                folderMap[key].items.push(item);
            }
        });

        return Object.values(folderMap).map(({ folder, items }) => (
            <React.Fragment key={folder.id + folder.path}>
                <Cell style={{ paddingLeft: '10px', backgroundColor: '#F8FAFC' }}>
                    <Icon src={FOLDER_YELLOW_SVG} alt="folder" />
                    <Label style={{ fontWeight: '600' }}>{folder.name}</Label>
                </Cell>
                {items.map(item => (
                    <Cell key={item.id} style={{ paddingLeft: '30px' }}>
                        <Icon src={FILE_SVG} alt="file" />
                        <Label>{item.name}</Label>
                    </Cell>
                ))}
            </React.Fragment>
        ));
    }

    const renderReportSetting = () => {
        if (!setting || !showSettingSection) {
            return null;
        }
        return <Cell>
            <ContentWrap>
                <Label>학습 설정</Label>
                <Content>
                    <Wrap>
                        <Label>단어 학습</Label>
                        <SettingWrap>
                            {setting.word.focus ? <Ul>
                                <Li>{`단어 듣기: ${setting.word.word_repeat}번`}</Li>
                                <Li>{`예문 듣기: ${setting.word.example_repeat}번`}</Li>
                            </Ul> : null}
                            {setting.word.write ? <Ul><Li>{`낱말 쓰기: ${setting.word.word_write}단계`}</Li></Ul> : null}
                            {setting.word.painting ? <Ul><Li>{`그림 맞추기`}</Li></Ul> : null}
                            <Ul><Li>{`커트라인(오답): ${setting.word.cutline}개`}</Li></Ul>
                        </SettingWrap>
                    </Wrap>
                    <Wrap>
                        <Label>문장 학습</Label>
                        <SettingWrap>
                            {setting.sentence.listening ? <Ul>
                                <Li>{`문장 듣기: ${setting.sentence.sentence_repeat}번`}</Li>
                            </Ul> : null}
                            {setting.sentence.order ? <Ul>
                                <Li>{`빈칸: ${setting.sentence.blank}%`}</Li>
                                <Li>{`통과율: ${setting.sentence.pass}%`}</Li>
                            </Ul> : null}
                            <Ul><Li>{`커트라인(오답): ${setting.sentence.cutline}개`}</Li></Ul>
                        </SettingWrap>
                    </Wrap>
                    <Wrap>
                        <Label>동영상 학습</Label>
                        <SettingWrap>
                            <Ul><Li>{`메인 동영상 보기: ${setting.video.main_repeat}번`}</Li></Ul>
                            <Ul><Li>{`녹음 동영상 보기: ${setting.video.video_repeat}번`}</Li></Ul>
                        </SettingWrap>
                    </Wrap>
                </Content>
                {onDeleteSettingClickHandler && (
                    <SmallButton name="삭제" onButtonClick={onDeleteSettingClickHandler} />
                )}
            </ContentWrap>
        </Cell>
    }

    const renderColumns = () => {
        const columns = [];
        
        if (showBranchColumn) {
            columns.push(
                <Column key="branch">
                    <HeaderWithSearch>
                        <HeaderTitle>{branchHeader}</HeaderTitle>
                        <CountPill>{selectedBranchList.length}</CountPill>
                        <SearchBox>
                            <FiSearch size={16} style={{marginRight:4, color:'#A0AEC0'}} />
                            <StyledSearchInput
                                type="text"
                                placeholder="이름 검색"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </SearchBox>
                    </HeaderWithSearch>
                    {viewBranchList()}
                </Column>
            );
        }
        
        columns.push(
            <Column key="report">
                <Header>{reportHeader}<CountPill>{selectedItems.length}</CountPill></Header>
                {viewFolderList(folderList)}
            </Column>
        );
        
        columns.push(
            <Column key="selected">
                <Header>{selectedHeader}<CountPill>{selectedItems.length}</CountPill></Header>
                {renderSelectedReport()}
                {renderReportSetting()}
            </Column>
        );
        
        return columns;
    };

    return (
        <Container>
            <TableContainer>
                <Modal isOpen={isOpenErrorModal} title="ERROR" content="폴더에 과제가 없습니다" onClose={onErrorModalConfirmHandler} />
                {renderColumns()}
            </TableContainer>
            <DateContainer>
                <DateLabel>기간설정</DateLabel>
                <DateInput type="date" name="start" value={effectiveDate.start} onChange={(e) => onEffectiveDateChangeHandler(e.target.name, e.target.value)} min={effectiveDateSize ? effectiveDateSize.start : ""} max={effectiveDateSize ? effectiveDate.end : ""} />
                {showEndDate && (
                    <>
                        ~
                        <DateInput type="date" name="end" value={effectiveDate.end} onChange={(e) => onEffectiveDateChangeHandler(e.target.name, e.target.value)} min={effectiveDateSize ? effectiveDateSize.start : ""} max={effectiveDateSize ? effectiveDate.end : ""} />
                    </>
                )}
                {showScheduleButtons && (
                    <ButtonGroup>
                        {onOpenSettingModalClickHandler && (
                            <SmallButton name="학습설정변경" onButtonClick={onOpenSettingModalClickHandler} />
                        )}
                        {onOpenScheduleModalClickHandler && (
                            <SmallButton name="일괄일정관리" onButtonClick={onOpenScheduleModalClickHandler} />
                        )}
                        {onOpenDateScheduleModalClickHandler && (
                            <SmallButton name="날짜지정일정관리" onButtonClick={onOpenDateScheduleModalClickHandler} />
                        )}
                    </ButtonGroup>
                )}
            </DateContainer>
        </Container>
    );
};
const Container = styled.div`
    display: flex;
    flex-direction: column;
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 8px 24px rgba(15, 23, 42, 0.04);
    overflow: hidden;
    min-height: 0;
    flex: 1;
`

const TableContainer = styled.div`
    display: flex;
    overflow: hidden;
    background-color: white;
    min-height: 0;
    flex: 1;
`

const Column = styled.div`
    flex: 1;
    min-width: 0;
    min-height: 380px;
    max-height: calc(100vh - 340px);
    overflow-y: auto;
    overflow-x: hidden;
    border-right: 1px solid #E2E8F0;

    &:last-child {
        border-right: none;
    }

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #F8FAFC;
    }

    &::-webkit-scrollbar-thumb {
        background: #CBD5E1;
        border-radius: 3px;
    }
`

const Header = styled.div`
    background-color: #FAFBFC;
    font-weight: 700;
    text-align: center;
    padding: 14px 16px;
    border-bottom: 1px solid #E2E8F0;
    position: sticky;
    z-index: 1;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #0F172A;
    font-size: 14px;
`

const Cell = styled.div<{ $active?: boolean }>`
    padding: 11px 16px;
    border-bottom: 1px solid #F1F5F9;
    text-align: left;
    display: flex;
    gap: 10px;
    background-color: ${props => props.$active ? "#FEF2F2" : "white"};
    align-items: center;
    transition: background-color 0.15s;
    box-shadow: ${props => props.$active ? "inset 3px 0 0 #DF0000" : "none"};

    &:hover {
        background-color: ${props => props.$active ? "#FEF2F2" : "#F8FAFC"};
    }
`

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-grow: 1;
    align-items: flex-start;
    border-right: 1px solid #E2E8F0;
    padding: 4px 12px;

    &:last-child {
        border-right: none;
    }
`

const CheckBox = styled.input`
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #DF0000;
    flex-shrink: 0;
`

const Icon = styled.img`
    width: 18px;
    height: 18px;
    opacity: 0.9;
    flex-shrink: 0;
`

const Ul = styled.ul`
    padding: 0;
    margin: 0;
    list-style: none;
`

const Li = styled.li`
    font-size: 12px;
    text-align: left;
    color: #64748B;
    line-height: 1.5;
`

const DateContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    padding: 14px 20px;
    border-top: 1px solid #E2E8F0;
    background-color: #FAFBFC;
    gap: 12px;
`

const Label = styled.label`
    color: #0F172A;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const Tri = styled.img`
    transition: transform 0.2s ease;
    cursor: pointer;
    opacity: 0.55;
    width: 12px;
    flex-shrink: 0;

    &:hover {
        opacity: 1;
    }
`

const DateLabel = styled.span`
    color: #0F172A;
    font-size: 13px;
    font-weight: 700;
`

const DateInput = styled.input`
    padding: 8px 12px;
    border: 1px solid #E2E8F0;
    font-size: 13px;
    border-radius: 8px;
    color: #0F172A;
    background: white;
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: #DF0000;
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }

    &::-webkit-calendar-picker-indicator {
        cursor: pointer;
        opacity: 0.6;
        transition: opacity 0.2s;

        &:hover {
            opacity: 1;
        }
    }
`

const ContentWrap = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 12px;
    padding: 8px 0;
    width: 100%;
`

const Content = styled.div`
    display: flex;
    flex-grow: 1;
    gap: 12px;
`

const SettingWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const ButtonGroup = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const HeaderWithSearch = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background-color: #FAFBFC;
    font-weight: 700;
    padding: 8px 12px;
    border-bottom: 1px solid #E2E8F0;
    position: sticky;
    z-index: 1;
    top: 0;
    min-height: 52px;
`;

const HeaderTitle = styled.div`
    font-size: 14px;
    color: #0F172A;
    white-space: nowrap;
`;

const SearchBox = styled.div`
    display: flex;
    align-items: center;
    background: white;
    border-radius: 8px;
    padding: 4px 8px;
    border: 1px solid #E2E8F0;
    min-width: 0;
`;

const StyledSearchInput = styled.input`
    border: none;
    background: transparent;
    font-size: 13px;
    color: #0F172A;
    outline: none;
    width: 110px;
    padding: 4px 0;
    &::placeholder {
        color: #94A3B8;
    }
`;

const CountPill = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #DF0000;
    background: #FEF2F2;
    border-radius: 999px;
    padding: 2px 8px;
`;

const EmptyHint = styled.div`
    padding: 32px 16px;
    text-align: center;
    color: #94A3B8;
    font-size: 13px;
    line-height: 1.5;
`;

export default ReleaseTable;